// src/pages/Mafia/mafiaPdfText.js
//
// Text for the MAFIA PDFs (MAFIA Table, Digital Inspection Record).
//
// "Additional Info" was typed for the fonts of the legacy program (Mafia2013,
// InspectionXpert GDT). It holds characters the built-in font of a PDF cannot
// draw. jsPDF then writes the WHOLE text two bytes per character: the letters
// come out spaced ("Ø . 6 2 0"), the odd character as something else ("A/F"
// printed as "Aà F"), and the text runs out of its cell because its width was
// measured wrongly. About 66,000 lines of the two databases hold such a
// character (counted 2026-09-29).
//
//   cleanPdfText     what is the same in every font: control characters out,
//                    and the private-use characters of the InspectionXpert
//                    fonts replaced by the sign those fonts draw for them.
//   useSymbolFont    DejaVu Sans Condensed draws the drawing symbols
//                    (▪ ∕ ⌀ ↧ □ ≤ …); what it has no drawing for is replaced
//                    by the nearest sign it has.
//   toPlainPdfText   only when the symbol font could not be loaded: the text
//                    is brought down to what the built-in font can draw.
//
// No asset is imported here, so the functions also run outside the browser
// (the PDF test).

export const PDF_SYMBOL_FONT = "DejaVuSansCondensed";
const PDF_SYMBOL_FONT_FILE = "DejaVuSansCondensed.ttf";

// Private-use characters of "INSPECTIONXPERT GDT FRAMES / NOFRMS" -> the sign
// the font draws for them (read from the font files). In those fonts the usual
// places of these signs are taken by drawing symbols.
const LEGACY_FONT_SIGNS = {
  0xe000: "/", 0xe001: "'", 0xe002: '"', 0xe003: "!", 0xe004: "$", 0xe005: ",",
  0xe006: "<", 0xe007: ">", 0xe008: "#", 0xe009: "&", 0xe00a: "%", 0xe00b: "@",
  0xe00c: "?", 0xe00d: ":", 0xe00e: ";", 0xe00f: "*", 0xe010: "=", 0xe011: "\\",
  0xe01a: "Ⓔ", 0xe01b: "Ⓡ", 0xe01c: "Ⓤ", 0xe01f: "℄",
  0xe020: "a", 0xe021: "d", 0xe022: "e", 0xe023: "f", 0xe024: "g", 0xe025: "h",
  0xe026: "j", 0xe027: "k", 0xe028: "m", 0xe029: "n", 0xe02a: "p", 0xe02b: "r",
  0xe02c: "s",
};

// What the symbol font has no drawing for -> the nearest it has.
const NEAREST_IN_SYMBOL_FONT = {
  0x2316: "⊕",   // position            -> circled plus
  0x2334: "⊔",   // counterbore         -> square cup
  0x2335: "∨",   // countersink         -> v shape
  0x2b1c: "□",   // large white square  -> white square
  0x24c2: "(M)", 0x24ba: "(E)", 0x24c1: "(L)", 0x24c5: "(P)", 0x24c8: "(S)",
  0x24c9: "(T)", 0x24ca: "(U)", 0x24bb: "(F)", 0x24c7: "®",
};

// The built-in fonts (WinAnsi): what jsPDF can write one byte per character.
const WIN_ANSI_EXTRA = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160,
  0x2039, 0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
]);
const inBuiltInFont = (code) =>
  code === 10 || (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WIN_ANSI_EXTRA.has(code);

// Only for toPlainPdfText.
const PLAIN = {
  0x25aa: "•", 0x25ab: "•", 0x2215: "/", 0x2044: "/", 0x039c: "M", 0x2300: "Ø",
  0x03a6: "Ø", 0x2264: "<=", 0x2265: ">=", 0x2502: "|", 0x02da: "°", 0x030a: "°",
  0x2070: "°", 0x2032: "'", 0x2033: '"', 0x2212: "-", 0x2194: "<->", 0x02c5: "v",
  0x25ca: "<>", 0x2104: "CL",
};

/**
 * Control characters out, legacy private-use characters replaced by their
 * sign. Line breaks stay. Never throws; null / undefined give "".
 */
export function cleanPdfText(value) {
  if (value == null) return "";
  const text = String(value);
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code === 13) continue;                                  // CR: the line feed does the break
    if (code === 10) { out += "\n"; continue; }
    if (code === 9 || code === 0xa0) { out += " "; continue; }  // tab, no-break space
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) continue; // control characters
    if (code === 0x200b || code === 0x200c || code === 0x200d || code === 0xfeff) continue; // zero width
    if (code >= 0xd800 && code <= 0xdfff) { out += "?"; if (code <= 0xdbff) i++; continue; } // beyond the BMP
    if (code >= 0xe000 && code <= 0xf8ff) { out += LEGACY_FONT_SIGNS[code] || "?"; continue; }
    out += text[i];
  }
  return out;
}

/** True when the built-in font of a PDF can draw every character of the text. */
export function builtInFontCanDraw(text) {
  for (let i = 0; i < text.length; i++) if (!inBuiltInFont(text.charCodeAt(i))) return false;
  return true;
}

/** The text for the BUILT-IN font: nothing in it makes jsPDF write two bytes per character. */
export function toPlainPdfText(value) {
  const text = cleanPdfText(value);
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (inBuiltInFont(code)) { out += text[i]; continue; }
    const near = NEAREST_IN_SYMBOL_FONT[code];
    if (PLAIN[code]) out += PLAIN[code];
    else if (near && builtInFontCanDraw(near)) out += near;
    else out += "?";
  }
  return out;
}

/**
 * Puts the symbol font into the document. Returns { font, fit } — `fit(text)`
 * gives the text with every character the font can draw — or null when there
 * is no font or jsPDF refuses it (the caller then uses toPlainPdfText).
 *
 * @param {object} doc         the jsPDF document
 * @param {string|null} base64 the font file, base64
 */
export function useSymbolFont(doc, base64) {
  if (!doc || !base64) return null;
  try {
    const current = doc.getFont();
    doc.addFileToVFS(PDF_SYMBOL_FONT_FILE, base64);
    doc.addFont(PDF_SYMBOL_FONT_FILE, PDF_SYMBOL_FONT, "normal");
    doc.setFont(PDF_SYMBOL_FONT, "normal");
    const metadata = doc.getFont().metadata;
    doc.setFont(current.fontName, current.fontStyle);
    if (!metadata || typeof metadata.characterToGlyph !== "function") return null;

    const draws = (code) => code === 10 || code === 32 || metadata.characterToGlyph(code) > 0;
    const fit = (value) => {
      const text = cleanPdfText(value);
      let out = "";
      for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        if (draws(code)) { out += text[i]; continue; }
        const near = NEAREST_IN_SYMBOL_FONT[code];
        if (near && [...near].every((ch) => draws(ch.charCodeAt(0)))) out += near;
        else out += "?";
      }
      return out;
    };
    return { font: PDF_SYMBOL_FONT, fit };
  } catch (e) {
    return null;
  }
}
